import { ZERO_COST } from 'constants/currency';
import { AUTO_NOTE_STRATEGIES, HOUR_LAYOUT } from 'constants/hour';
import { RATE_TYPES } from 'constants/project';
import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import type { Filter as CalendarFilter } from 'redux/modules/calendar.types';
import { defaultState, defaultStateId } from 'redux/modules/hourStates.constants';
import type { HourState } from 'redux/modules/hourStates.schema';
import type { UserPropertyValue } from 'redux/modules/user.types';
import { enrichProjectVariant } from 'redux/selectors/utils/projects';
import type { Currency } from 'types/account';
import type { EnrichedHourDraft, Hour, HourAsCard, HourDraft, HourTimestamp } from 'types/hour';
import type { EnrichedProject, LightProject, Project } from 'types/project';
import type { SuggestionAsCard } from 'types/suggestion';
import type { CostWithCurrency, Duration } from 'types/units';
import type { UnionToIntersection } from 'types/utility';
import { timestamp } from 'utils';
import { fromISO } from 'utils/luxon-helpers';
import { datetimeTools, dayTools } from 'utils/time';
import { formatCost } from './currency';
import { isSameMonth } from './date-helper';
import { calculateDuration } from './durations';
import { adjustTimestampsToDuration, calculateDurationFromTimestamps } from './timestamps';

const SECONDS_IN_24H = 86400;

/**
 * FIXME: This is a stopgap measure to fix an issue with old data, where the timer is no longer
 * running but for whatever reason the `timerRunningSince` timestamp hasn't been cleared from the
 * database. We introduce an artificial upper limit of 24 hours to the timestamp and drop data
 * that's older than that, since we currently (September 2020) have an upper limit of 13 hours for
 * running timers. But this _should_ be fixed by cleaning up the data. When that's done, this
 * function is no longer needed and should be reverted back to the old code.
 *
 * @see https://github.com/Timely/project-management/issues/70
 */
const clampSecondsOnTimer = (timerRunningSince?: number) => {
  const secondsOnTimer = timerRunningSince ? timestamp() - timerRunningSince : 0;
  return secondsOnTimer >= SECONDS_IN_24H ? 0 : secondsOnTimer;
};

// Change an Hour's Day attribute
// If the day has a start time and an end time
// this will set a new start time and a new end time.

interface HourLike {
  day?: string;
  from: string | null;
  to: string | null;
  timestamps?: Array<HourLike>;
}
export const changeDay = <T extends HourLike>(base: T, day: string): T => {
  dayTools.validateDay(day);

  const applyChange = (hour: HourLike): HourLike => {
    const change = { ...hour };

    if (hour.from) {
      change.from = datetimeTools.setDay(hour.from, day);
    }

    if (change.from && hour.from && hour.to) {
      const seconds = datetimeTools.secondsBetween(hour.from, hour.to);
      change.to = datetimeTools.addSeconds(change.from, seconds);
    }

    return change;
  };

  const output = { ...applyChange(base), day };

  if (base.timestamps) {
    output.timestamps = base.timestamps
      .map(applyChange)
      .map(timestamp => ({ ...timestamp, entry_ids: [] }));
  }

  return output as T;
};

export const secondsToPxSteps = (totalSeconds: number, isTimerRunning: boolean = false) => {
  const totalMinutes = totalSeconds / 60;
  const steps = Math.floor(totalMinutes / HOUR_LAYOUT.stepMinutes);
  const height = steps * HOUR_LAYOUT.stepHeight + HOUR_LAYOUT.baseHeight;
  const minHeight = isTimerRunning ? HOUR_LAYOUT.minHeightWithTimer : HOUR_LAYOUT.minHeight;
  return height < minHeight ? minHeight : height;
};

export function pxToSeconds(height: number) {
  const steps = (height - HOUR_LAYOUT.baseHeight) / HOUR_LAYOUT.stepHeight;
  const minutes = Math.round(steps) * HOUR_LAYOUT.stepMinutes;
  return Math.max(0, minutes * 60);
}

export function handleSmallHeights(loggedSecs: number, timerRunning?: boolean) {
  if (timerRunning) {
    return secondsToPxSteps(loggedSecs, true);
  }

  const fifteenMinutes = 900;
  const oneHourFifteen = 4500;

  // For very small durations (up to 15 minutes), use the smallest height (2 lines)
  if (loggedSecs <= fifteenMinutes) {
    return 50; // Smallest height (2 lines only)
  }
  // For durations between 15min and 1h15m, use linear interpolation
  else if (loggedSecs <= oneHourFifteen) {
    // Linear interpolation between 15min (50px) and 1h15m (70px)
    const range = oneHourFifteen - fifteenMinutes;
    const progress = loggedSecs - fifteenMinutes;
    const ratio = progress / range;

    // Interpolate from 50px to 70px (3 full lines)
    return Math.round(50 + ratio * 20);
  }

  // For durations over 1h15m, use the standard calculation
  return secondsToPxSteps(loggedSecs, timerRunning);
}

export const hourCardSizes = (
  hour: HourAsCard | SuggestionAsCard,
  sizeBasedOnDuration: boolean = true,
  staticSize: boolean = false
) => {
  if (staticSize) {
    return {
      heightPx: 80,
      loggedHeightPx: 80,
      plannedHeightPx: 80,
      numNoteLines: 1,
      numClientLines: 1,
      hasSpaceForClient: true,
    };
  }
  if (!sizeBasedOnDuration) {
    return {
      heightPx: HOUR_LAYOUT.minHeight,
      loggedHeightPx: HOUR_LAYOUT.minHeight,
      plannedHeightPx: HOUR_LAYOUT.minHeight,
      numNoteLines: 1,
      numClientLines: 0,
      hasSpaceForClient: false,
    };
  }
  const { numLoggedSecondsExact, numPlannedSeconds, timerRunningSince, isTimerRunning } = hour;

  const secondsOnTimer = clampSecondsOnTimer(timerRunningSince);

  const totalLoggedSeconds = numLoggedSecondsExact + secondsOnTimer;
  const isJustPlanned = numPlannedSeconds > 0 && !totalLoggedSeconds;
  // Calculate pixel sizing:
  const loggedHeightPx = handleSmallHeights(totalLoggedSeconds, isTimerRunning); // issue here?
  const plannedHeightPx = secondsToPxSteps(numPlannedSeconds);

  const loggedHeight =
    isTimerRunning && loggedHeightPx < HOUR_LAYOUT.minHeightWithTimer
      ? HOUR_LAYOUT.minHeightWithTimer
      : loggedHeightPx;

  // Set height based on planned time, if no logged time exists
  const heightPx = isJustPlanned ? plannedHeightPx : loggedHeight;

  // Calculate # lines we can show
  const totalHeight = heightPx <= HOUR_LAYOUT.minHeight ? HOUR_LAYOUT.minContentHeight : heightPx;
  const contentHeight = totalHeight - HOUR_LAYOUT.footerHeight;
  const lineHeight = HOUR_LAYOUT.cssFontSize * HOUR_LAYOUT.cssLineHeight;
  const lines = Math.max(Math.floor(contentHeight / lineHeight - 1), HOUR_LAYOUT.maxContentLines);
  const numNoteLines = lines > 1 ? Math.floor(lines / 2) : lines; // make room for project, client

  const clientShowsAtSeconds =
    60 *
    (isTimerRunning ? 60 + numNoteLines * 20 : HOUR_LAYOUT.minMinutesRequiredForProjectNameToShow);
  const spaceSecondsToCompare = isJustPlanned ? numPlannedSeconds : totalLoggedSeconds;
  const hasSpaceForClient = isTimerRunning
    ? lines > 1 && spaceSecondsToCompare > clientShowsAtSeconds
    : true;

  // Set numClientLines to 0 if loggedHeightPx is less than minHeightWithClient
  const numClientLines =
    loggedHeightPx < HOUR_LAYOUT.minHeightWithClient
      ? 0
      : hasSpaceForClient
        ? lines - numNoteLines
        : 0;

  return {
    heightPx,
    loggedHeightPx,
    plannedHeightPx,
    numNoteLines,
    numClientLines,
    hasSpaceForClient,
  };
};

// Copied from utils/event, update to use HourAsCard format + seconds as base:
export interface SummarizeCostAndDurationOutput {
  /**
   * Total duration, no rounding or flooring to closest minute per hour.
   * If ignoreRunningTimer=true, the entire hour with running timer is exluded from the total duration
   */
  duration: Duration;
  /**
   * Total duration where each hour is rounded down to the closest minute (floored)
   * Example: 01m50s + 01m45s = 02m00s (only in seconds..)
   */
  durationRoundedDown: Duration;
  estimatedDuration: Duration;
  cost: CostWithCurrency;
  estimatedCost: CostWithCurrency;
  mixedCurrencies: boolean;
}

/**
 * Calculate the sums and duration of all hours
 * Optionally pass in filter (mode & date) to exclude "outside days"
 */
export function summarizeCostAndDuration(
  hours: Array<HourAsCard> = [],
  options: {
    filter?: CalendarFilter;
    ignoreRunningTimer?: boolean;
  } = {}
): SummarizeCostAndDurationOutput {
  let seconds = 0;
  let secondsFloored = 0;
  let plannedSeconds = 0;
  let cost = 0;
  let plannedCost = 0;

  const filter = options.filter || null;

  let prevCurrency: Currency | undefined;
  let mixedCurrencies = false;

  for (const {
    day,
    numLoggedSecondsExact,
    numPlannedSeconds,
    numLoggedMoney,
    numPlannedMoney,
    isTimerRunning,
    timerRunningSince,
    costPerSecond,
    projectCurrency,
  } of hours) {
    // "Outside days" are days before & after the first & last day of filter.date's month
    const isOutsideDay = filter && filter.span === 'month' && !isSameMonth(day, filter.date);
    const shouldIgnore = !!options.ignoreRunningTimer && isTimerRunning;

    if (isOutsideDay || shouldIgnore) continue;

    const secondsOnTimer = clampSecondsOnTimer(timerRunningSince);

    secondsFloored += Math.floor((numLoggedSecondsExact + secondsOnTimer) / 60) * 60;
    seconds += numLoggedSecondsExact + secondsOnTimer;
    plannedSeconds += numPlannedSeconds;
    cost += numLoggedMoney + secondsOnTimer * costPerSecond;
    plannedCost += numPlannedMoney;

    if (prevCurrency) {
      mixedCurrencies ||= prevCurrency.id !== projectCurrency.id;
    }

    prevCurrency = projectCurrency;
  }

  return {
    duration: calculateDuration(seconds),
    durationRoundedDown: calculateDuration(secondsFloored),
    estimatedDuration: calculateDuration(plannedSeconds),
    cost: !mixedCurrencies ? formatCost(cost, prevCurrency) : ZERO_COST,
    estimatedCost: !mixedCurrencies ? formatCost(plannedCost, prevCurrency) : ZERO_COST,
    mixedCurrencies,
  };
}

/**
 * Returns the hourly rate for the user who owns the Hour.
 */
export function getHourRateForHour(hour: Omit<EnrichedHourDraft, 'context'>) {
  const { project, id, hour_rate } = hour;

  // if the hour has already been saved and has an hour rate, we should always respect it
  // as it might have a historical rate that is different than what the user has now
  if (id && hour_rate) {
    return hour_rate;
  }

  const users = project?.fullProject?.users;
  const baseRate = project?.fullProject?.hour_rate ?? 0;

  // Global (ie, non user specific) rates?
  if (project.fullProject?.rate_type !== RATE_TYPES.USER_RATE) {
    return baseRate;
  }

  // Do we have the project<->user relationship? Find record that matches owner of Hour:
  const userOnProject = !!users && users.find(({ user_id }) => user_id === hour.user.id);
  // Return the user specific hour rate
  return userOnProject ? userOnProject.hour_rate : baseRate;
}

export function enrichProjectsOnHours(
  hours: Array<Hour<Project | LightProject>>,
  pinnedProjectIds: Set<number>
): Array<Hour<EnrichedProject>> {
  return hours.map(hour => enrichProjectOnHour(hour, pinnedProjectIds));
}

export function enrichProjectOnHour(
  hour: Hour<Project | LightProject>,
  pinnedProjectIds: Set<number>
): Hour<EnrichedProject> {
  const variant =
    (hour.project as UnionToIntersection<typeof hour.project>).label_ids != null
      ? { type: 'full', project: hour.project }
      : { type: 'light', lightProject: hour.project };
  const enriched = enrichProjectVariant(variant as any, pinnedProjectIds);
  return {
    ...hour,
    project: enriched,
  };
}

export const parseAutoNoteStrategy = (strategy: UserPropertyValue) => {
  return AUTO_NOTE_STRATEGIES.find(s => s === strategy);
};

/**
 * When the timestamps change, recalculate their total duration.
 * When the duration is manually changed, shrink or expand the timestamps.
 */
export function synchronizeDurationAndTimestamps(
  base: HourDraft,
  changes: Partial<HourDraft>
): Partial<HourDraft> {
  const isClearingTimestamps = changes.timestamps?.length === 0;
  const hasChangedTimestamps =
    !!changes.timestamps && !isEqual(changes.timestamps, base.timestamps);

  if (hasChangedTimestamps && !isClearingTimestamps) {
    return { ...changes, duration: calculateDurationFromTimestamps(changes.timestamps!) };
  } else {
    const hasChangedDuration = !!changes.duration && !isEqual(changes.duration, base.duration);
    const hasTimestamps = base.timestamps?.length > 0;

    if (hasChangedDuration && hasTimestamps && !isClearingTimestamps) {
      const newDuration = changes.duration?.total_seconds || 0;
      return {
        ...changes,
        timestamps: adjustTimestampsToDuration(base.timestamps, newDuration),
      };
    }
  }

  return changes;
}

type HourWithState = Pick<Hour<unknown>, 'state'>;

function isHour(maybeHour: HourWithState | HourState): maybeHour is HourWithState {
  return 'state' in maybeHour;
}

/**
 * Cache isHourLocked calls for Hour objects as they are performed frequently during batch
 * selections. It should be safe to key on this because when a permissions are changed for a state,
 * the hours with that state will also be updated, and changing the role ID requires a reload, so we
 * should never get stale data here.
 */
const hourCache = new WeakMap<HourWithState, boolean>();

/**
 * Checks if an hour should be locked. If an Hour is passed, the result will be cached.
 */
export function isHourLocked(
  hourOrState: HourWithState | HourState,
  options: {
    states?: Record<number, HourState | undefined>;
    userRoleId: number;
    userIsTeamLead: boolean;
  }
): boolean {
  let hourState: HourState;
  const _isHour = isHour(hourOrState);
  if (_isHour) {
    if (hourCache.has(hourOrState)) {
      return hourCache.get(hourOrState)!;
    }

    hourState =
      options.states?.[hourOrState.state?.id ?? defaultStateId] ??
      hourOrState.state ??
      defaultState;
  } else {
    hourState = hourOrState;
  }

  let result: boolean;
  if (hourState.id === defaultStateId) {
    result = false;
  } else {
    result =
      hourState.locked ||
      !hourState.permissions.some(
        p => p.role_id === options.userRoleId || (p.team_lead && options.userIsTeamLead)
      );
  }

  if (_isHour) {
    hourCache.set(hourOrState, result);
  }
  return result;
}

/**
 * Reconstructs timestamps for an hour with empty timestamps array
 * @param hour The existing hour with empty timestamps
 * @returns An array with a single timestamp object
 */
export function constructTimestampsContainingTicMemoriesForHourIfMissing(
  hour: Pick<Hour<number>, 'day' | 'timestamps' | 'updated_at' | 'duration'>
): HourTimestamp[] {
  // If nothing to change, bail early..
  if (hour.timestamps.length > 0) return hour.timestamps;
  if (hour.duration.total_seconds === 0) return [];

  const updatedAt = DateTime.fromSeconds(hour.updated_at).setZone('UTC') as DateTime<true>;
  const toTimestamp = fromISO(`${hour.day}T${updatedAt.toFormat('HH:mm:ss')}Z`).setZone(
    'UTC'
  ) as DateTime<true>;
  const fromTimestamp = toTimestamp.minus({ seconds: hour.duration.total_seconds });

  return [
    {
      from: fromTimestamp.toISO({ suppressMilliseconds: true }),
      to: toTimestamp.toISO({ suppressMilliseconds: true }),
      entry_ids: [],
    },
  ];
}

/**
 * Checks if note needs to be generated again for a draft.
 * @param draft - The draft to check
 * @returns True if the note needs to be generated again, false otherwise
 */
export function shouldRegenerateNoteForDraft(
  draft: HourDraft,
  changes: Partial<HourDraft>
): boolean {
  const draftEntryIds = draft.timestamps?.flatMap(timestamp => timestamp.entry_ids) ?? [];
  const changesEntryIds = changes.timestamps?.flatMap(timestamp => timestamp.entry_ids) ?? [];

  const entryIdsChanged = !isEqual(draftEntryIds, changesEntryIds);
  const durationChanged = !isEqual(draft.duration, changes.duration);
  const hasSelectedMemories = changesEntryIds.length > 0;

  //We only care about duration changes if there are no selected memories
  return entryIdsChanged || (durationChanged && !hasSelectedMemories);
}
